import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) nav("/admin", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email.trim(), password);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome to the Atelier.");
      nav("/admin", { replace: true });
    } else {
      toast.error(r.error || "Invalid credentials");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-brand-bone" data-testid="admin-login-page">
      <div className="relative hidden md:block overflow-hidden bg-brand-green">
        <img src="/pdf-images/page-01.jpg" alt="Atelier" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-green/80 via-brand-green/60 to-brand-charcoal/80" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-brand-bone">
          <div>
            <span className="text-[0.72rem] tracking-[0.32em] uppercase text-brand-gold">
              Dealer Portal
            </span>
            <h1 className="mt-6 font-serif text-6xl leading-[0.98]">
              Atelier
              <span className="block italic text-brand-gold">by ViewLux.</span>
            </h1>
          </div>
          <p className="text-brand-bone/70 max-w-sm">
            Access your dashboard of quote requests, configured doors, and customer details.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          onSubmit={submit}
          className="w-full max-w-md"
        >
          <Link to="/" className="text-[0.62rem] tracking-[0.32em] uppercase text-brand-ink/60 hover:text-brand-green">
            ← Back to site
          </Link>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl text-brand-green">
            Sign in.
          </h2>
          <p className="mt-2 text-brand-ink/60">Enter your admin credentials to continue.</p>

          <div className="mt-8 space-y-4">
            <label className="block">
              <span className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-ink/60">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 w-full bg-white border border-brand-ink/20 px-4 py-3 focus:outline-none focus:border-brand-green"
                data-testid="admin-email"
                placeholder="admin@atelierdoors.com"
              />
            </label>
            <label className="block">
              <span className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-ink/60">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 w-full bg-white border border-brand-ink/20 px-4 py-3 focus:outline-none focus:border-brand-green"
                data-testid="admin-password"
                placeholder="••••••••••"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={busy}
            className={`mt-8 btn-primary ${busy ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="admin-login-submit"
          >
            {busy ? "Signing in…" : "Sign in →"}
          </button>
        </motion.form>
      </div>
    </div>
  );
}
